/**
 * Inter-Site Transfers.
 *
 * A Head of Stores operation with no approval loop, but it must be raised
 * against an auto-approved IST requisition. Stock leaves the source site the
 * moment the transfer is initiated and lands at the target site on delivery.
 * Neither leg counts as consumption in the weekly report.
 */
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { ArrowLeft, ArrowRight, Pencil, Repeat, Send, Truck } from 'lucide-react'
import {
  Alert, Card, Confirm, EmptyState, Field, Input, Loading, Modal, Select, StatusPill, Textarea, Th, sortRows, useSort, useToast,
} from '@/components/ui'
import {
  AttachmentCollector, AttachmentList, NotPermitted, queueAttachments, type PendingAttachment,
} from '@/components/docs/shared'
import {
  TimberTally, blankTallyRow, isTimber, tallyCft, tallyFromJson, tallyToJson,
  type TallyRow,
} from '@/components/docs/TimberTally'
import { useAuth } from '@/lib/auth'
import { DeleteDocument } from '@/components/docs/DeleteDocument'
import { fetchLines, fetchOne, useMaterials, usePeople, useQuery, useSiteNames, useSites, useStock } from '@/lib/data'
import { supabase } from '@/lib/supabase'
import { DocumentDate, todayIso } from '@/components/docs/DocumentDate'
import { can, iso } from '@/lib/domain'
import { ddmmyyyy, dateTime, qty } from '@/lib/format'
import { enqueue, reviseDocument } from '@/lib/sync'
import type { Material } from '@/lib/types'

const STAGES = ['DRAFT', 'TRANSFER_INITIATED', 'IN_TRANSIT', 'DELIVERED'] as const

/**
 * One line of an IST requisition, with what is genuinely left to place on a
 * transfer.
 *
 * Read from v_ist_pr_allocation rather than from pr_line_items, because
 * pr_line_items.quantity_on_ist only rises when goods are DELIVERED. Between
 * raising a draft and the lorry arriving, the requisition line still reads as
 * untouched — which is how the same forty bags could be put on two transfers
 * and neither screen object.
 */
type AllocLine = {
  pr_line_id: string
  pr_id: string
  sl_no: number
  material_id: string
  item_code: string
  item_name: string
  uom: string
  quantity_requested: number
  qty_delivered: number
  qty_committed: number
  qty_open: number
  open_transfer_count: number
  open_transfer_ids: string | null
}

/** A store that holds a material this requisition still needs. */
type SourceOption = {
  pr_line_id: string
  material_id: string
  from_site_id: string
  from_site_name: string
  available: number
  can_supply: number
}

/** Quantities are numeric(14,3) in the database; keep the arithmetic there too. */
const r3 = (n: number) => Math.round(n * 1000) / 1000

/**
 * The paperwork for ONE consignment.
 *
 * Every source store issues its own delivery challan and its own e-way bill,
 * because the goods physically leave a different gate. So these travel per
 * source, not per requisition.
 */
type ConsignmentDoc = {
  challanNo: string
  challanDate: string
  receivedDate: string
  ewayBill: string
  vehicleNo: string
  transporter: string
}
// todayIso is a function, not a constant — it must be CALLED. The old code got
// away with `useState(todayIso)` because React treats a function argument as a
// lazy initialiser and calls it for you. Nothing does that here.
const blankDoc = (): ConsignmentDoc => ({
  challanNo: '', challanDate: todayIso(), receivedDate: '',
  ewayBill: '', vehicleNo: '', transporter: '',
})

export function IstList() {
  const { profile } = useAuth()
  const role = profile!.role
  const { data, loading, error } = useQuery<any>('inter_site_transfers', () =>
    supabase.from('inter_site_transfers').select('*').order('created_at', { ascending: false }).limit(300))

  const { sort, toggle } = useSort('created_at')
  const sorted = useMemo(() => sortRows(data, sort, {
    created_at: 'date', initiated_at: 'date', delivered_at: 'date', challan_date: 'date',
  }), [data, sort])

  return (
    <div className="space-y-4">
      <Card
        title="Inter-Site Transfers"
        subtitle="Moving stock between OVAL stores instead of buying it again"
        actions={can(role, 'manageIst') && (
          <Link to="/ists/new" className="btn-primary btn-sm"><Repeat className="h-4 w-4" /> New transfer</Link>
        )}
      >
        <Alert tone="info">
          Stock is deducted from the source site as soon as the transfer is <strong>initiated</strong>, and added
          to the target site only when it is marked <strong>delivered</strong>. Neither leg is treated as
          consumption — the material has moved, not been used.
        </Alert>
      </Card>

      <Card bodyClass="p-0 sm:p-0">
        {loading && <Loading />}
        {error && <div className="p-4"><EmptyState title="Could not load transfers" message={error} /></div>}
        {!loading && !error && data.length === 0 && (
          <EmptyState icon={<Repeat className="h-9 w-9" />} title="No transfers yet"
                      message="The Head of Stores raises these from an auto-approved IST requisition." />
        )}
        {data.length > 0 && (
          <div className="table-wrap mx-0">
            <table className="tbl">
              <thead>
                <tr><Th id="id" sort={sort} onSort={toggle}>Transfer</Th><Th id="from_site_id" sort={sort} onSort={toggle}>From</Th><th /><Th id="to_site_id" sort={sort} onSort={toggle}>To</Th><Th id="status" sort={sort} onSort={toggle}>Status</Th>
                    <Th id="challan_no" sort={sort} onSort={toggle}>Challan</Th><Th id="initiated_at" sort={sort} onSort={toggle}>Initiated</Th><Th id="delivered_at" sort={sort} onSort={toggle}>Delivered</Th><Th id="ist_pr_id" sort={sort} onSort={toggle}>Against PR</Th></tr>
              </thead>
              <tbody>
                {sorted.map((t: any) => (
                  <tr key={t.id}>
                    <td><Link to={`/ists/${t.id}`} className="font-mono text-xs font-semibold text-oval-700 hover:underline">{t.id}</Link></td>
                    <td className="text-xs">{t.from_site_id}</td>
                    <td><ArrowRight className="h-3 w-3 text-slate-300" /></td>
                    <td className="text-xs">{t.to_site_id}</td>
                    <td><StatusPill status={t.status} /></td>
                    <td className="text-xs">{t.challan_no ?? '—'}</td>
                    <td className="text-xs">{t.initiated_at ? ddmmyyyy(t.initiated_at) : '—'}</td>
                    <td className="text-xs">{t.delivered_at ? ddmmyyyy(t.delivered_at) : '—'}</td>
                    <td className="font-mono text-xs text-slate-500">{t.ist_pr_id}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  )
}

// ---------------------------------------------------------------------------
export function IstNew() {
  const { profile } = useAuth()
  const navigate = useNavigate()
  const toast = useToast()
  const [params] = useSearchParams()
  const { id: editId } = useParams()
  const editing = !!editId
  const [loadingDoc, setLoadingDoc] = useState(editing)
  // A form that failed to load must not be offered for editing.
  const [editBlocked, setEditBlocked] = useState(false)

  const { data: sites } = useSites()
  const { siteName } = useSiteNames()
  const { data: materials } = useMaterials()

  const [istPrId, setIstPrId] = useState(params.get('pr') ?? '')
  const [istPrs, setIstPrs] = useState<any[]>([])
  const [alloc, setAlloc] = useState<AllocLine[]>([])
  const [sources, setSources] = useState<SourceOption[]>([])
  // Transfers already raised against this same requisition. One requisition met
  // from three stores is three transfers; this is how the operator sees which
  // ones he has already done.
  const [siblings, setSiblings] = useState<any[]>([])
  // What THIS draft already holds, when correcting one. The allocation view
  // counts a draft as committed, so without adding its own quantities back the
  // screen would tell the operator he has nothing left to transfer.
  const [originalQty, setOriginalQty] = useState<Record<string, string>>({})
  /**
   * WHICH STORE EACH LINE IS COMING FROM — the operator's plan, held in the
   * browser and never sent anywhere.
   *
   * This exists because of a fault reported on 08-09-2026. The source cards are
   * drawn under each material, so clicking "EK Jatan" under Cement plainly
   * means "the cement comes from Ek Jatan". It did not: it set the source for
   * the WHOLE transfer and then filled every line Ek Jatan happened to stock,
   * which silently pulled both TMT lines in with it.
   *
   * Now a click assigns ONE line, and every store named in the plan becomes a
   * transfer of its own when the form is submitted.
   */
  const [plan, setPlan] = useState<Record<string, string>>({})
  /**
   * The timber tally per requisition line, keyed the same way as `quantities`.
   * On a measured material the quantity IS the footed sheet, so the two are
   * always written together and can never drift apart.
   */
  const [tallies, setTallies] = useState<Record<string, TallyRow[]>>({})
  /**
   * The challan, e-way bill and vehicle for EACH source store, keyed by site.
   *
   * Reported 08-09-2026: the plan named two stores, and pressing Create made
   * one transfer. That was my design and it was wrong — the operator had said
   * on one screen where all three materials were coming from, and expected the
   * screen to act on what he had said.
   *
   * The reason it was built one-at-a-time is real and unchanged: two stores
   * means two lorries, and two lorries cannot share a challan number. So the
   * answer is not to merge them into one document, it is to ask for both
   * challans here and write both documents on one press.
   */
  const [docs, setDocs] = useState<Record<string, ConsignmentDoc>>({})
  const [fromSite, setFromSite] = useState('')
  const [toSite, setToSite] = useState('')
  const [remarks, setRemarks] = useState('')
  const [quantities, setQuantities] = useState<Record<string, string>>({})
  const [attachments, setAttachments] = useState<PendingAttachment[]>([])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const errorRef = useRef<HTMLDivElement>(null)

  const { data: sourceStock } = useStock(fromSite || null)
  const byId = useMemo(
    () => Object.fromEntries(materials.map((m) => [m.id, m])) as Record<string, Material>, [materials])

  // Correcting a draft transfer before the stock has moved.
  useEffect(() => {
    if (!editId) return
    void (async () => {
      setLoadingDoc(true)
      try {
        const { error: guard } = await supabase.rpc('rpc_assert_editable', {
          p_doc_type: 'IST', p_doc_id: editId,
        })
        if (guard) throw new Error(guard.message)

        const { data: h } = await supabase.from('inter_site_transfers')
          .select('*').eq('id', editId).single()
        if (!h) throw new Error(`Transfer ${editId} was not found.`)
        const { data: rows } = await supabase.from('ist_line_items')
          .select('*').eq('ist_id', editId).order('sl_no')

        setIstPrId(h.ist_pr_id ?? '')
        setFromSite(h.from_site_id ?? '')
        setToSite(h.to_site_id ?? '')
        // A saved transfer is exactly one consignment, keyed by its own source.
        setDocs({ [h.from_site_id ?? '']: {
          challanNo: h.challan_no ?? '',
          challanDate: h.challan_date ?? '',
          receivedDate: h.received_date ?? '',
          ewayBill: h.eway_bill_no ?? '',
          vehicleNo: h.vehicle_no ?? '',
          transporter: h.transporter ?? '',
        } })
        setRemarks(h.remarks ?? '')
        const own = Object.fromEntries(
          (rows ?? []).map((r: any) => [r.pr_line_id, String(r.quantity ?? '')]),
        ) as Record<string, string>
        setQuantities(own)
        setOriginalQty(own)
        setTallies(Object.fromEntries(
          (rows ?? []).filter((r: any) => r.timber_tally)
            .map((r: any) => [r.pr_line_id, tallyFromJson(r.timber_tally)]),
        ))
        setError(null)
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Could not open this transfer for correction')
        setEditBlocked(true)
      } finally { setLoadingDoc(false) }
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editId])

  useEffect(() => {
    void (async () => {
      const { data } = await supabase.from('purchase_requisitions').select('*')
        .eq('pr_type', 'IST').in('status', ['APPROVED', 'PARTIALLY_FULFILLED'])
        .order('date_created', { ascending: false })
      setIstPrs(data ?? [])
    })()
  }, [])

  useEffect(() => {
    if (!istPrId) { setAlloc([]); setSources([]); setSiblings([]); setPlan({}); return }
    void (async () => {
      const [a, s, t] = await Promise.all([
        supabase.from('v_ist_pr_allocation').select('*').eq('pr_id', istPrId).order('sl_no'),
        supabase.from('v_ist_source_options').select('*').eq('pr_id', istPrId),
        supabase.from('inter_site_transfers')
          .select('id, from_site_id, status, challan_no, challan_date')
          .eq('ist_pr_id', istPrId).order('created_at'),
      ])
      setAlloc((a.data ?? []) as AllocLine[])
      setSources((s.data ?? []) as SourceOption[])
      setSiblings(t.data ?? [])
      const pr = istPrs.find((p) => p.id === istPrId)
      if (pr) setToSite(pr.site_id) // the IST requisition belongs to the receiving site
      // Deliberately NO pre-fill.
      //
      // It used to fill every line with its whole outstanding quantity the
      // moment the requisition was chosen. When the cement comes from one store
      // and the steel from another — which is the ordinary case, not the odd one
      // — the operator had to notice that, and delete two of three lines by
      // hand, with nothing on screen telling him he had to. Naming the source
      // store is what decides which lines can travel, so the quantities follow
      // the source, not the requisition.
      if (!editing) { setQuantities({}); setPlan({}); setDocs({}); setTallies({}); setFromSite('') }
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [istPrId, istPrs.length, editing])

  if (!can(profile!.role, 'manageIst')) {
    return <NotPermitted what="raise an inter-site transfer" who="Head of Stores" />
  }

  /**
   * How much of this line a NEW transfer may still carry.
   *
   * qty_open already nets off what has been delivered and what sits on other
   * live transfers. When correcting a draft, that draft's own quantity is part
   * of "committed", so it is added back — otherwise editing 40 down to 30 would
   * be refused for exceeding an allowance of zero.
   */
  function openFor(l: AllocLine): number {
    return r3(l.qty_open + (editing ? Number(originalQty[l.pr_line_id] ?? 0) : 0))
  }

  /** Live stock of this material at the chosen source store. */
  function onHandAt(materialId: string): number {
    return sourceStock.find((s) => s.material_id === materialId)?.quantity_on_hand ?? 0
  }

  /** How much store `siteId` holds of the material on this requisition line. */
  function availableAt(prLineId: string, siteId: string): number {
    return sources.find((o) => o.pr_line_id === prLineId && o.from_site_id === siteId)?.available ?? 0
  }

  /** The paperwork block for a source store, creating a blank one on demand. */
  function docFor(siteId: string): ConsignmentDoc {
    return docs[siteId] ?? blankDoc()
  }
  function setDocField(siteId: string, key: keyof ConsignmentDoc, value: string) {
    setDocs((d) => ({ ...d, [siteId]: { ...(d[siteId] ?? blankDoc()), [key]: value } }))
  }

  /**
   * Assign ONE line to ONE store. Clicking the same store again unassigns it.
   *
   * Touches nothing but the line whose card was clicked, and fills that line's
   * quantity to the lesser of what is still needed and what that store holds.
   * Every other line is left exactly as it was.
   */
  function assign(prLineId: string, siteId: string) {
    if (editing) return
    const nextPlan = { ...plan }
    const nextQty = { ...quantities }

    if (nextPlan[prLineId] === siteId) {
      delete nextPlan[prLineId]
      delete nextQty[prLineId]
    } else {
      nextPlan[prLineId] = siteId
      const line = alloc.find((l) => l.pr_line_id === prLineId)
      const mat = line ? byId[line.material_id] : null
      if (isTimber(mat)) {
        // Timber is never pre-filled with a volume. The store-man counts the
        // pieces going on the lorry and the sheet foots them.
        delete nextQty[prLineId]
        setTallies((t) => (t[prLineId] ? t : { ...t, [prLineId]: [blankTallyRow(mat)] }))
      } else {
        const take = r3(Math.min(availableAt(prLineId, siteId), line?.qty_open ?? 0))
        if (take > 0) nextQty[prLineId] = String(take)
        else delete nextQty[prLineId]
      }
      // A store newly named in the plan needs a challan of its own.
      setDocs((d) => (d[siteId] ? d : { ...d, [siteId]: blankDoc() }))
    }

    setPlan(nextPlan)
    setQuantities(nextQty)
    // fromSite is only ever a display hint now — the plan is what is submitted.
    const stores = Array.from(new Set(Object.values(nextPlan)))
    setFromSite(stores.length === 1 ? stores[0] : '')
  }

  /**
   * The "add a source store" dropdown — the escape hatch for anyone who would
   * rather not click through the planner.
   *
   * It assigns every line this store can supply that has NOT already been given
   * to somewhere else. It never steals a line the operator has assigned.
   */
  function pickSource(siteId: string) {
    if (editing) { setFromSite(siteId); return }
    if (!siteId) return
    const nextPlan = { ...plan }
    const nextQty = { ...quantities }
    for (const l of alloc) {
      if (nextPlan[l.pr_line_id]) continue
      const take = r3(Math.min(availableAt(l.pr_line_id, siteId), l.qty_open))
      if (take <= 0) continue
      nextPlan[l.pr_line_id] = siteId
      if (isTimber(byId[l.material_id])) {
        setTallies((t) => (t[l.pr_line_id] ? t : { ...t, [l.pr_line_id]: [blankTallyRow(byId[l.material_id])] }))
      } else {
        nextQty[l.pr_line_id] = String(take)
      }
    }
    setDocs((d) => (d[siteId] ? d : { ...d, [siteId]: blankDoc() }))
    setPlan(nextPlan)
    setQuantities(nextQty)
    const stores = Array.from(new Set(Object.values(nextPlan)))
    setFromSite(stores.length === 1 ? stores[0] : '')
  }

  /** Drop a whole consignment from the plan. */
  function dropConsignment(siteId: string) {
    const nextPlan = { ...plan }
    const nextQty = { ...quantities }
    for (const [lineId, s] of Object.entries(plan)) {
      if (s !== siteId) continue
      delete nextPlan[lineId]
      delete nextQty[lineId]
      setTallies((t) => { const n = { ...t }; delete n[lineId]; return n })
    }
    setPlan(nextPlan)
    setQuantities(nextQty)
    const stores = Array.from(new Set(Object.values(nextPlan)))
    setFromSite(stores.length === 1 ? stores[0] : '')
  }

  /** The lines the plan has given to one store, with a quantity on them. */
  function linesFor(siteId: string): AllocLine[] {
    return alloc.filter((l) => plan[l.pr_line_id] === siteId
                            && Number(quantities[l.pr_line_id]) > 0)
  }

  function validate(): string | null {
    if (!istPrId) return 'Select the auto-approved IST requisition these transfers fulfil. A transfer cannot exist without one.'
    if (!toSite) return 'Select the site the material is going to.'

    // ---- correcting one saved transfer -------------------------------------
    if (editing) {
      if (!fromSite) return 'Select the site the material is coming from.'
      if (fromSite === toSite) return 'The source and destination sites must be different.'
      const picked = alloc.filter((l) => Number(quantities[l.pr_line_id]) > 0)
      if (picked.length === 0) return 'Enter a quantity for at least one material.'
      for (const l of picked) {
        const want = Number(quantities[l.pr_line_id])
        if (want > openFor(l) + 0.001) {
          return `${l.item_name}: only ${qty(openFor(l))} ${l.uom} of this line is still to be transferred.`
        }
        if (want > onHandAt(l.material_id) + 0.001) {
          return `${l.item_name}: only ${qty(onHandAt(l.material_id))} in stock at the source site.`
        }
      }
      if (!docFor(fromSite).challanNo.trim()) return 'Enter the delivery challan number for the transfer.'
      return null
    }

    // ---- creating, one transfer per source store ---------------------------
    if (consignments.length === 0) {
      return 'Choose a store for at least one material. Click a store card under the material it is coming from.'
    }
    for (const [site] of consignments) {
      const name = siteName(site)
      if (site === toSite) return `${name} is the destination. Material cannot be transferred to itself.`
      const lines = linesFor(site)
      if (lines.length === 0) {
        return `The transfer from ${name} has no quantity on any line. Enter one, or remove that store from the plan.`
      }
      for (const l of lines) {
        const want = Number(quantities[l.pr_line_id])
        if (want > openFor(l) + 0.001) {
          return `${l.item_name}: only ${qty(openFor(l))} ${l.uom} of this line is still to be transferred`
            + `${l.open_transfer_ids ? ` — ${qty(l.qty_committed)} is already on ${l.open_transfer_ids}` : ''}`
            + `. Reduce the quantity, or cancel that transfer first.`
        }
        const have = availableAt(l.pr_line_id, site)
        if (want > have + 0.001) {
          return `${l.item_name}: ${name} holds only ${qty(have)} ${l.uom}.`
        }
      }
      if (!docFor(site).challanNo.trim()) {
        return `Enter the delivery challan number for the transfer from ${name}. `
          + `Each lorry carries its own challan — they cannot share one.`
      }
    }
    return null
  }

  async function create() {
    const problem = validate()
    if (problem) {
      setError(problem)
      // Take him to it. The Submit button sits at the foot of a form that can
      // run to several screens, so an error drawn at the top is, from where he
      // is standing, nothing happening at all. Reported on the goods receipt
      // screen 09-09-2026; the same shape of form, the same trap.
      requestAnimationFrame(() => errorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }))
      return
    }
    setError(null); setBusy(true)
    try {
      // ---- correcting a draft transfer -------------------------------------
      if (editing) {
        const picked = alloc.filter((l) => Number(quantities[l.pr_line_id]) > 0)
        const d = docFor(fromSite)
        await reviseDocument({
          docType: 'IST',
          docId: editId!,
          header: {
            from_site_id: fromSite,
            to_site_id: toSite,
            challan_no: d.challanNo,
            challan_date: d.challanDate || null,
            received_date: d.receivedDate || null,
            eway_bill_no: d.ewayBill || null,
            vehicle_no: d.vehicleNo || null,
            transporter: d.transporter || null,
            remarks: remarks || null,
          },
          lines: picked.map((l, i) => ({
            sl_no: i + 1,
            material_id: l.material_id,
            uom: byId[l.material_id]?.unit_of_measure ?? l.uom ?? 'NO',
            quantity: Number(quantities[l.pr_line_id]),
            pr_line_id: l.pr_line_id,
            timber_tally: tallyToJson(tallies[l.pr_line_id] ?? []),
          })),
          // An IST is not an approval document — it stays a draft until the
          // Head of Stores presses "Initiate transfer".
          submit: false,
        })
        if (attachments.length) await queueAttachments('IST', editId!, attachments)
        toast.success('Transfer updated. Open it and press "Initiate transfer" when the lorry leaves.')
        navigate(`/ists/${editId}`)
        return
      }

      // ---- creating: ONE TRANSFER PER SOURCE STORE -------------------------
      //
      // Written one after another rather than in parallel. Each is an
      // independent draft, so if the second fails the first is still a real
      // document the operator can see and work with — and the message below
      // tells him exactly which ones were made. A Promise.all here would leave
      // him guessing.
      const made: string[] = []
      for (const [site] of consignments) {
        const lines = linesFor(site)
        const d = docFor(site)
        const localId = `IST-LOCAL-${Date.now()}-${made.length}`
        const entry = await enqueue({
          kind: 'ist.create', docType: 'IST', localDocId: localId,
          label: `Transfer from ${siteName(site)} to ${siteName(toSite)}`,
          payload: {
            header: {
              ist_pr_id: istPrId,
              from_site_id: site,
              to_site_id: toSite,
              status: 'DRAFT',
              initiated_by: profile!.id,
              initiator_email: profile!.email,
              challan_no: d.challanNo,
              challan_date: d.challanDate || null,
              received_date: d.receivedDate || null,
              eway_bill_no: d.ewayBill || null,
              vehicle_no: d.vehicleNo || null,
              transporter: d.transporter || null,
              remarks: remarks || null,
            },
            lines: lines.map((l, i) => ({
              sl_no: i + 1,
              material_id: l.material_id,
              uom: byId[l.material_id]?.unit_of_measure ?? l.uom ?? 'NO',
              quantity: Number(quantities[l.pr_line_id]),
              pr_line_id: l.pr_line_id,
              timber_tally: tallyToJson(tallies[l.pr_line_id] ?? []),
            })),
          },
        })
        // The same challan photograph rarely covers two lorries, so the files
        // are attached to the first transfer only. The rest can be attached
        // from each transfer's own screen.
        if (made.length === 0 && attachments.length) {
          await queueAttachments('IST', entry.server_doc_id ?? localId, attachments)
        }
        made.push(siteName(site))
      }

      toast.success(made.length === 1
        ? 'Transfer created as a draft. Open it and press "Initiate transfer" to move the stock out.'
        : `${made.length} transfers created as drafts — one each from ${made.join(' and ')}. `
          + 'Open each one and press "Initiate transfer" when its lorry leaves.')
      navigate('/ists')
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not create the transfer')
    } finally { setBusy(false) }
  }

  // The plan, grouped into the consignments it implies. One entry per source
  // store, in the order the operator assigned them. Plain arithmetic rather
  // than a useMemo: it is a dozen rows, and a hook here would sit below an
  // early return.
  const consignments: Array<[string, AllocLine[]]> = (() => {
    const m = new Map<string, AllocLine[]>()
    for (const l of alloc) {
      const s = plan[l.pr_line_id]
      if (!s) continue
      m.set(s, [...(m.get(s) ?? []), l])
    }
    return [...m.entries()]
  })()
  const unplanned = alloc.filter((l) => l.qty_open > 0 && !plan[l.pr_line_id])

  if (loadingDoc) return <Loading label="Opening the transfer…" />

  if (editBlocked) {
    return (
      <div className="space-y-4">
        <Alert tone="error" title="This transfer cannot be corrected">{error}</Alert>
        <button className="btn-secondary" onClick={() => navigate(`/ists/${editId}`)}>
          Back to the transfer
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <Card
        title={editing ? `Correct transfer ${editId}` : 'New inter-site transfer'}
        subtitle={editing
          ? 'The stock has not moved yet, so this can still be changed. It keeps its number.'
          : 'Against an auto-approved IST requisition you raised earlier'}
      >
        <div ref={errorRef}>{error && <div className="mb-4"><Alert tone="error">{error}</Alert></div>}</div>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <Field label="IST requisition" required className="lg:col-span-3"
                 hint="Only requisitions of type IST, which are auto-approved because you raised them yourself">
            <Select
              value={istPrId} onChange={(e) => setIstPrId(e.target.value)}
              placeholder={istPrs.length ? 'Select the IST requisition…' : 'No open IST requisitions — create one from a submitted PR first'}
              options={istPrs.map((p) => ({ value: p.id, label: `${p.id} · for ${siteName(p.site_id)} · ${ddmmyyyy(p.date_created)}` }))}
            />
          </Field>
          <Field label={editing ? 'Transfer from (source store)' : 'Add a source store'}
                 required={editing}
                 hint={editing
                   ? 'Stock is deducted here on initiation'
                   : 'Optional shortcut — assigns every unassigned line this store can supply. Choosing store by store below is usually clearer.'}>
            <Select value={editing ? fromSite : ''} onChange={(e) => pickSource(e.target.value)}
                    placeholder={editing ? 'Select source site…' : 'Assign remaining lines to…'}
                    options={sites.filter((s) => s.id !== toSite).map((s) => ({ value: s.id, label: s.site_name }))} />
          </Field>
          <Field label="Transfer to (destination)" required hint="Stock is credited here on delivery">
            <Select value={toSite} onChange={(e) => setToSite(e.target.value)} placeholder="Select destination site…"
                    options={sites.filter((s) => s.id !== fromSite).map((s) => ({ value: s.id, label: s.site_name }))} />
          </Field>
          {/* The challan, e-way bill and vehicle belong to a consignment, not to
              the requisition — two stores means two lorries and two challans. So
              when creating they are asked for once per store, further down. When
              correcting a saved transfer there is exactly one, and it stays
              here where it has always been. */}
          {editing && (<>
            <Field label="Delivery challan no." required>
              <Input value={docFor(fromSite).challanNo}
                     onChange={(e) => setDocField(fromSite, 'challanNo', e.target.value)} />
            </Field>
            <DocumentDate label="Challan date (goods left)" value={docFor(fromSite).challanDate}
                          onChange={(v) => setDocField(fromSite, 'challanDate', v)} />
            <DocumentDate label="Delivered on (goods arrived)" value={docFor(fromSite).receivedDate}
                          onChange={(v) => setDocField(fromSite, 'receivedDate', v)} required={false}
                          hint="Leave blank if it arrived the same day. Stock is credited to the destination on this date." />
            <Field label="E-way bill no.">
              <Input value={docFor(fromSite).ewayBill}
                     onChange={(e) => setDocField(fromSite, 'ewayBill', e.target.value)} />
            </Field>
            <Field label="Vehicle number">
              <Input value={docFor(fromSite).vehicleNo}
                     onChange={(e) => setDocField(fromSite, 'vehicleNo', e.target.value)} />
            </Field>
            <Field label="Transporter">
              <Input value={docFor(fromSite).transporter}
                     onChange={(e) => setDocField(fromSite, 'transporter', e.target.value)} />
            </Field>
          </>)}
        </div>
      </Card>

      {/* ------------------------------------------------------------------
          Where is this stock?

          The Head of Stores' real question, answered for every line at once
          instead of being guessed one site at a time. Clicking a store names
          it as the source AND fills only the lines it can supply — so one
          requisition met from two stores is two clicks and two transfers,
          not a hunt through eighteen sites.
         ------------------------------------------------------------------ */}
      {istPrId && !editing && alloc.some((l) => l.qty_open > 0) && (
        <Card title="Where does each material come from?"
              subtitle="Choose a store for EACH line on its own. Lines from the same store travel together on one challan; a second store means a second transfer.">
          <div className="space-y-3">
            {alloc.filter((l) => l.qty_open > 0).map((l) => {
              const opts = sources.filter((o) => o.pr_line_id === l.pr_line_id)
                .sort((a, b) => b.can_supply - a.can_supply)
              const chosen = plan[l.pr_line_id]
              return (
                <div key={l.pr_line_id}
                     className={`rounded-lg border p-3 ${chosen ? 'border-oval-300 bg-oval-50/40' : 'border-slate-200'}`}>
                  <div className="mb-2 flex flex-wrap items-baseline gap-x-2">
                    <span className="text-sm font-medium">{l.item_name}</span>
                    <span className="font-mono text-xs text-slate-500">{l.item_code}</span>
                    <span className="text-xs text-slate-500">
                      · still to source <strong className="text-slate-800">{qty(l.qty_open)} {l.uom}</strong>
                      {l.qty_committed > 0 && <> · {qty(l.qty_committed)} already on {l.open_transfer_ids}</>}
                      {l.qty_delivered > 0 && <> · {qty(l.qty_delivered)} delivered</>}
                    </span>
                    {chosen && (
                      <span className="text-xs font-medium text-oval-700">
                        → from {siteName(chosen)} <span className="text-slate-400">(click again to undo)</span>
                      </span>
                    )}
                  </div>
                  {opts.length === 0 ? (
                    <p className="text-xs text-red-600">
                      No other store holds this. It cannot be transferred — it has to be purchased.
                    </p>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {opts.map((o) => (
                        <button key={o.from_site_id} type="button"
                                onClick={() => assign(l.pr_line_id, o.from_site_id)}
                                title={chosen === o.from_site_id
                                  ? `${l.item_name} is coming from ${o.from_site_name}. Click to undo.`
                                  : `Take ${l.item_name} — and only ${l.item_name} — from ${o.from_site_name}`}
                                className={`rounded-md border px-2.5 py-1.5 text-left text-xs transition ${
                                  chosen === o.from_site_id
                                    ? 'border-oval-500 bg-oval-100 text-oval-900 ring-1 ring-oval-400'
                                    : 'border-slate-200 hover:border-oval-300 hover:bg-oval-50'}`}>
                          <span className="block font-medium">{o.from_site_name}</span>
                          <span className="block text-slate-500">
                            has {qty(o.available)} {l.uom} · can send {qty(o.can_supply)}
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )
            })}

            {unplanned.length > 0 && consignments.length > 0 && (
              <p className="text-xs text-amber-700">
                Still without a store: {unplanned.map((x) => x.item_name).join(', ')}.
                Those lines will not be on any transfer.
              </p>
            )}

            {siblings.length > 0 && (
              <p className="text-xs text-slate-500">
                Already raised against this requisition:{' '}
                {siblings.map((t: any, i: number) => (
                  <span key={t.id}>
                    {i > 0 && ', '}
                    <Link className="font-mono text-oval-700 hover:underline" to={`/ists/${t.id}`}>{t.id}</Link>
                    {' '}from {siteName(t.from_site_id)} ({t.status.replace(/_/g, ' ').toLowerCase()})
                  </span>
                ))}
              </p>
            )}
          </div>
        </Card>
      )}

      {/* ------------------------------------------------------------------
          THE DOCUMENTS THAT WILL BE WRITTEN.

          One block per source store, each with its own challan, e-way bill and
          vehicle, because that is what physically leaves each gate. Pressing
          Create writes every one of them.

          Reported 08-09-2026: the plan named two stores and only one transfer
          appeared. That was the screen creating whatever consignment happened
          to be loaded, and ignoring the rest of a plan the operator had already
          made. Now the plan IS the submission.
         ------------------------------------------------------------------ */}
      {!editing && consignments.length > 0 && (
        <Card title={consignments.length === 1
                ? 'The transfer that will be created'
                : `The ${consignments.length} transfers that will be created`}
              subtitle="One document per source store. Each carries its own delivery challan and e-way bill — two lorries cannot share one.">
          <div className="space-y-4">
            {consignments.map(([site, lines], i) => (
              <div key={site} className="rounded-lg border border-slate-300">
                <div className="flex flex-wrap items-center justify-between gap-2 rounded-t-lg bg-slate-50 px-3 py-2">
                  <span className="flex items-center gap-2 text-sm font-semibold text-slate-800">
                    <span className="rounded bg-oval-600 px-1.5 py-0.5 font-mono text-[11px] text-white">
                      {i + 1} of {consignments.length}
                    </span>
                    from {siteName(site)}
                    <ArrowRight className="h-3.5 w-3.5 text-slate-400" />
                    {siteName(toSite) || 'destination'}
                  </span>
                  <button type="button" className="text-xs text-red-600 hover:underline"
                          onClick={() => dropConsignment(site)}>
                    remove this transfer
                  </button>
                </div>

                <div className="space-y-3 p-3">
                  <div className="table-wrap">
                    <table className="tbl">
                      <thead>
                        <tr><th>Material</th><th className="num">Still to source</th>
                            <th className="num">At {siteName(site)}</th><th className="num">Transfer quantity</th></tr>
                      </thead>
                      <tbody>
                        {lines.map((l) => {
                          const have = availableAt(l.pr_line_id, site)
                          const want = Number(quantities[l.pr_line_id]) || 0
                          const over = want > have + 0.001 || want > openFor(l) + 0.001
                          const mat = byId[l.material_id]
                          const timber = isTimber(mat)
                          return (
                            <Fragment key={l.pr_line_id}>
                            <tr className={over ? 'bg-red-50' : ''}>
                              <td>
                                <span className="block text-sm font-medium">{l.item_name}</span>
                                <span className="block font-mono text-xs text-slate-500">{l.item_code}</span>
                              </td>
                              <td className="num">{qty(openFor(l))} {l.uom}</td>
                              <td className={`num ${have > 0 ? 'text-emerald-700' : 'text-red-600'}`}>{qty(have)}</td>
                              <td className="num">
                                <input type="number" min="0" step="any" inputMode="decimal"
                                       readOnly={timber}
                                       title={timber ? 'Footed from the tally sheet below.' : undefined}
                                       className={`field w-32 text-right ${over ? 'field-error' : ''} ${
                                         timber ? 'cursor-not-allowed bg-amber-50 font-semibold' : ''}`}
                                       value={quantities[l.pr_line_id] ?? ''}
                                       onChange={(e) => setQuantities((q) => ({ ...q, [l.pr_line_id]: e.target.value }))} />
                              </td>
                            </tr>
                            {timber && mat && (
                              <tr>
                                <td colSpan={4} className="p-2">
                                  <TimberTally material={mat} rows={tallies[l.pr_line_id] ?? []}
                                    onChange={(rows) => {
                                      setTallies((t) => ({ ...t, [l.pr_line_id]: rows }))
                                      setQuantities((q) => ({
                                        ...q,
                                        [l.pr_line_id]: rows.length ? String(tallyCft(rows)) : '',
                                      }))
                                    }} />
                                </td>
                              </tr>
                            )}
                            </Fragment>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>

                  <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    <Field label="Delivery challan no." required>
                      <Input value={docFor(site).challanNo}
                             onChange={(e) => setDocField(site, 'challanNo', e.target.value)} />
                    </Field>
                    <DocumentDate label="Challan date (goods left)" value={docFor(site).challanDate}
                                  onChange={(v) => setDocField(site, 'challanDate', v)} />
                    <DocumentDate label="Delivered on (goods arrived)" value={docFor(site).receivedDate}
                                  onChange={(v) => setDocField(site, 'receivedDate', v)} required={false}
                                  hint="Leave blank if it arrived the same day." />
                    <Field label="E-way bill no.">
                      <Input value={docFor(site).ewayBill}
                             onChange={(e) => setDocField(site, 'ewayBill', e.target.value)} />
                    </Field>
                    <Field label="Vehicle number">
                      <Input value={docFor(site).vehicleNo}
                             onChange={(e) => setDocField(site, 'vehicleNo', e.target.value)} />
                    </Field>
                    <Field label="Transporter">
                      <Input value={docFor(site).transporter}
                             onChange={(e) => setDocField(site, 'transporter', e.target.value)} />
                    </Field>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {istPrId && editing && (
        <Card title="Materials to transfer"
              subtitle="Still to source is what this requisition may still put on a transfer — after what has been delivered and what is already on another transfer">
          {alloc.length === 0 && <p className="py-4 text-center text-sm text-slate-500">This requisition has no open lines.</p>}
          <div className="table-wrap">
            <table className="tbl">
              <thead>
                <tr><th>Material</th><th className="num">Requisitioned</th><th className="num">Still to source</th>
                    <th className="num">In source store</th><th className="num">Transfer quantity</th></tr>
              </thead>
              <tbody>
                {alloc.map((l) => {
                  const onHand = onHandAt(l.material_id)
                  const open = openFor(l)
                  const want = Number(quantities[l.pr_line_id]) || 0
                  const over = want > onHand + 0.001 || want > open + 0.001
                  return (
                    <tr key={l.pr_line_id} className={over ? 'bg-red-50' : ''}>
                      <td>
                        <span className="block text-sm font-medium">{l.item_name}</span>
                        <span className="block font-mono text-xs text-slate-500">{l.item_code}</span>
                      </td>
                      <td className="num">{qty(l.quantity_requested)} {l.uom}</td>
                      <td className={`num ${open > 0 ? '' : 'text-slate-400'}`}>
                        {qty(open)}
                        {l.qty_committed > 0 && (
                          <span className="block text-xs font-normal text-amber-700"
                                title={`Committed on ${l.open_transfer_ids}`}>
                            {qty(l.qty_committed)} on {l.open_transfer_ids}
                          </span>
                        )}
                        {l.qty_delivered > 0 && (
                          <span className="block text-xs font-normal text-emerald-700">
                            {qty(l.qty_delivered)} delivered
                          </span>
                        )}
                      </td>
                      <td className={`num ${fromSite ? (onHand > 0 ? 'text-emerald-700' : 'text-red-600') : 'text-slate-400'}`}>
                        {fromSite ? qty(onHand) : 'select source'}
                      </td>
                      <td className="num">
                        <input type="number" min="0" step="any" inputMode="decimal"
                               className={`field w-32 text-right ${over ? 'field-error' : ''}`}
                               value={quantities[l.pr_line_id] ?? ''}
                               onChange={(e) => setQuantities((q) => ({ ...q, [l.pr_line_id]: e.target.value }))} />
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <Card title="Transport documents">
        <div className="space-y-4">
          <AttachmentCollector docType="IST" value={attachments} onChange={setAttachments}
                               hint="Delivery challan and e-way bill for the transfer vehicle." />
          <Field label="Remarks"><Textarea rows={2} value={remarks} onChange={(e) => setRemarks(e.target.value)} /></Field>
        </div>
      </Card>

      <div className="sticky bottom-20 z-10 flex justify-end gap-2 lg:bottom-4">
        <button className="btn-secondary" onClick={() => navigate('/ists')} disabled={busy}>Cancel</button>
        <button className="btn-primary" onClick={create} disabled={busy}>
          <Send className="h-4 w-4" />
          {editing
            ? 'Save transfer'
            : consignments.length > 1
              ? `Create ${consignments.length} transfers`
              : 'Create transfer'}
        </button>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
export function IstDetail() {
  const { id = '' } = useParams()
  const { profile } = useAuth()
  // Names, not email addresses — management 01-09-2026.
  const people = usePeople()
  const toast = useToast()
  const [ist, setIst] = useState<any>(null)
  const [lines, setLines] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [confirmStage, setConfirmStage] = useState<string | null>(null)
  // The date the goods actually arrived, asked for when the transfer is marked
  // delivered. It MUST be declared here with the other hooks, above the early
  // returns below — a useState placed after them runs on some renders and not
  // others, which is React error #310 and a blank screen.
  const [deliveredOn, setDeliveredOn] = useState('')

  const { data: materials } = useMaterials()
  const { data: sites } = useSites()
  const byId = useMemo(
    () => Object.fromEntries(materials.map((m) => [m.id, m])) as Record<string, Material>, [materials])

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const header = await fetchOne<any>('inter_site_transfers', id)
      if (!header) throw new Error(`Transfer ${id} was not found.`)
      setIst(header)
      setLines(await fetchLines<any>('ist_line_items', 'ist_id', id))
      setError(null)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load the transfer')
    } finally { setLoading(false) }
  }, [id])

  useEffect(() => { void load() }, [load])

  // Seed the arrival date once the transfer is on screen: whatever was entered
  // when it was raised, then the challan date, then today.
  useEffect(() => {
    if (!ist) return
    setDeliveredOn(ist.received_date ?? ist.challan_date ?? todayIso())
  }, [ist])

  if (loading) return <Loading />
  if (error || !ist) return <Alert tone="error">{error}</Alert>

  const stageIndex = STAGES.indexOf(ist.status)
  const allowed = can(profile!.role, 'manageIst')
  const next = stageIndex >= 0 && stageIndex < STAGES.length - 1 ? STAGES[stageIndex + 1] : null

  const stageCopy: Record<string, { label: string; message: string }> = {
    TRANSFER_INITIATED: {
      label: 'Initiate transfer',
      message: `The stock is deducted from ${ist.from_site_id} straight away. It will not appear at ${ist.to_site_id} until you mark the transfer delivered.`,
    },
    IN_TRANSIT: {
      label: 'Mark in transit',
      message: 'The vehicle has left the source site. No stock moves at this step — it simply records where the consignment is.',
    },
    DELIVERED: {
      label: 'Mark delivered',
      message: `The stock is added to ${ist.to_site_id}. This closes the transfer and settles the IST requisition line.`,
    },
  }

  async function advance(status: string, extra?: Record<string, unknown>) {
    await enqueue({
      kind: 'ist.status', docType: 'IST', localDocId: ist.id,
      label: `${ist.id} → ${status}`,
      payload: { id: ist.id, status, extra },
    })
    toast.success(`Transfer marked ${status.replace('_', ' ').toLowerCase()}.`)
    setConfirmStage(null)
    void load()
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <Link to="/ists" className="btn-ghost btn-sm"><ArrowLeft className="h-4 w-4" /> All transfers</Link>
          <h1 className="font-mono text-base font-bold text-slate-800">{ist.id}</h1>
          <StatusPill status={ist.status} />
        </div>
        {allowed && next && (
          <button className="btn-primary btn-sm" onClick={() => setConfirmStage(next)}>
            <Truck className="h-4 w-4" /> {stageCopy[next].label}
          </button>
        )}
      </div>

      {/* progress rail */}
      <Card>
        <ol className="flex flex-wrap items-center gap-2 text-xs">
          {STAGES.map((s, i) => (
            <li key={s} className="flex items-center gap-2">
              <span className={`rounded-full px-3 py-1 font-semibold ${
                i <= stageIndex ? 'bg-oval-700 text-white' : 'bg-slate-100 text-slate-500'}`}>
                {s.replace('_', ' ')}
              </span>
              {i < STAGES.length - 1 && <ArrowRight className="h-3 w-3 text-slate-300" />}
            </li>
          ))}
        </ol>
        {/*
          Two different things live here and they used to be confused.

          The EFFECTIVE date is the day the stock actually moved — the challan
          date going out, the delivery date coming in. That is what the ledger
          carries and what the weekly figures are built on.

          The RECORDED time is simply when somebody pressed the button, which
          on a back-dated transfer is weeks later. Showing only that, under a
          label saying "stock deducted", made it look as though a transfer
          dated in July had moved stock today.
        */}
        <div className="mt-3 grid gap-3 text-xs sm:grid-cols-3">
          <div>
            <p className="text-slate-500">Stock left {ist.from_site_id} on</p>
            <p className="text-sm font-bold text-slate-800">
              {ist.source_posted ? ddmmyyyy(ist.challan_date) : 'not yet'}
            </p>
            {ist.source_posted && (
              <p className="text-[11px] text-slate-400">recorded {dateTime(ist.initiated_at)}</p>
            )}
          </div>
          <div>
            <p className="text-slate-500">In transit since</p>
            <p className="text-sm font-bold text-slate-800">
              {ist.in_transit_at ? dateTime(ist.in_transit_at) : '—'}
            </p>
          </div>
          <div>
            <p className="text-slate-500">Stock reached {ist.to_site_id} on</p>
            <p className="text-sm font-bold text-slate-800">
              {ist.target_posted
                ? ddmmyyyy(ist.received_date ?? ist.challan_date)
                : 'not yet'}
            </p>
            {ist.target_posted && (
              <p className="text-[11px] text-slate-400">recorded {dateTime(ist.delivered_at)}</p>
            )}
          </div>
        </div>
      </Card>

      <Card title="Transfer details">
        <dl className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm sm:grid-cols-4">
          <D label="From" value={sites.find((s) => s.id === ist.from_site_id)?.site_name ?? ist.from_site_id} />
          <D label="To" value={sites.find((s) => s.id === ist.to_site_id)?.site_name ?? ist.to_site_id} />
          <D label="Against IST requisition" value={ist.ist_pr_id} />
          <D label="Initiated by" value={people.name({ email: ist.initiator_email })} />
          <D label="Challan" value={`${ist.challan_no ?? '—'} (${ddmmyyyy(ist.challan_date)})`} />
          <D label="Delivered on"
             value={ist.received_date ? ddmmyyyy(ist.received_date)
                                      : (ist.target_posted ? `${ddmmyyyy(ist.challan_date)} (same day)` : '—')} />
          <D label="E-way bill" value={ist.eway_bill_no ?? '—'} />
          <D label="Vehicle" value={ist.vehicle_no ?? '—'} />
          <D label="Transporter" value={ist.transporter ?? '—'} />
        </dl>
        {ist.status === 'DRAFT' && can(profile!.role, 'manageIst') && (
          <div className="mt-4 flex flex-wrap gap-2">
            <Link to={`/ists/${ist.id}/edit`} className="btn-secondary btn-sm">
              <Pencil className="h-4 w-4" /> Edit this draft
            </Link>
            {/* Transfers name their originator initiated_by, not creator_email. */}
            <DeleteDocument docType="IST" docId={ist.id} status={ist.status}
                            creatorId={ist.initiated_by} backTo="/ists" />
          </div>
        )}
      </Card>

      <AttachmentList docType="IST" docId={ist.id}
                      subtitle="Transfer challan, e-way bill and photographs" />

      <Card title="Materials in this transfer">
        <div className="table-wrap">
          <table className="tbl">
            <thead><tr><th>#</th><th>Material</th><th>Code</th><th className="num">Quantity</th></tr></thead>
            <tbody>
              {lines.map((l) => {
                const m = byId[l.material_id]
                return (
                  <tr key={l.id}>
                    <td className="text-xs text-slate-400">{l.sl_no}</td>
                    <td className="text-sm font-medium">{m?.item_name ?? l.material_id}</td>
                    <td className="font-mono text-xs text-slate-500">{m?.item_code ?? '—'}</td>
                    <td className="num font-semibold">{qty(l.quantity)} <span className="text-xs text-slate-400">{l.uom}</span></td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {confirmStage && confirmStage !== 'DELIVERED' && (
        <Confirm
          open
          title={stageCopy[confirmStage].label + '?'}
          message={stageCopy[confirmStage].message}
          confirmLabel={`Yes, ${stageCopy[confirmStage].label.toLowerCase()}`}
          tone="primary"
          onCancel={() => setConfirmStage(null)}
          onConfirm={() => advance(confirmStage)}
        />
      )}

      {/*
        Delivery gets its own dialog because it needs a date.

        When a transfer is raised you rarely know when it will arrive, so
        asking for the delivery date on the creation form only half works. It
        is asked for here instead, at the moment somebody actually knows the
        answer — and it is the date the receiving site's stock is credited on.
      */}
      {confirmStage === 'DELIVERED' && (
        <Modal
          open
          onClose={() => setConfirmStage(null)}
          title={`Mark ${ist.id} delivered?`}
          subtitle={`${ist.from_site_id} → ${ist.to_site_id}`}
          footer={<>
            <button className="btn-secondary" onClick={() => setConfirmStage(null)}>Cancel</button>
            <button
              className="btn-success"
              disabled={!deliveredOn
                || (!!ist.challan_date && deliveredOn < ist.challan_date)}
              onClick={() => advance('DELIVERED', {
                received_date: deliveredOn || ist.challan_date || null,
              })}
            >
              Yes, mark delivered
            </button>
          </>}
        >
          <p className="text-sm text-slate-700">{stageCopy.DELIVERED.message}</p>

          <div className="mt-4 max-w-xs">
            <DocumentDate
              label="Goods actually arrived on"
              value={deliveredOn}
              onChange={setDeliveredOn}
              hint={`Stock is credited to ${ist.to_site_id} on this date, not today.`}
            />
          </div>

          {ist.challan_date && deliveredOn && deliveredOn < ist.challan_date && (
            <div className="mt-3">
              <Alert tone="error" title="That is before the goods left">
                The challan is dated {ddmmyyyy(ist.challan_date)}. Material cannot arrive
                before it was despatched.
              </Alert>
            </div>
          )}
        </Modal>
      )}
    </div>
  )
}

const D = ({ label, value }: { label: string; value: string }) => (
  <div>
    <dt className="text-xs font-semibold text-slate-500">{label}</dt>
    <dd className="mt-0.5 break-words text-slate-800">{value}</dd>
  </div>
)
